import {useCallback, useEffect, useRef, useState} from 'react';
import type {APIState} from './types';

/**
 * Custom hook for fetching data from API endpoints
 * @template T - The type of data being fetched
 * @param url - The API endpoint URL
 * @param fileName - Name for logging purposes
 * @param skip - Whether to skip the initial fetch
 * @returns Object containing loading, error, data states and refetch function
 */
const useFetchData = <T>({
  url,
  fileName,
  skip = false,
}: {
  url: string;
  fileName: string;
  skip?: boolean;
}) => {
  const [fetchState, setFetchState] = useState<APIState<T>>({
    isLoading: !skip,
    isError: false,
    data: null,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const setLoading = useCallback((isLoading: boolean) => {
    setFetchState((prevState) => ({...prevState, isLoading}));
  }, []);

  const setError = useCallback((isError: boolean) => {
    setFetchState((prevState) => ({...prevState, isError}));
  }, []);

  const setData = useCallback((data: T | null) => {
    setFetchState((prevState) => ({...prevState, data: data ?? null}));
  }, []);

  const fetchQuery = useCallback(async () => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(false);
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Forwarded-Proto': 'https',
        },
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const body = (await response.json()) as unknown;
      setData(body as T);
      setLoading(false);
    } catch (error) {
      // Don't log error if request was aborted
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error(
          `${fileName.toUpperCase()}: something went wrong while querying ${url} with error: ${
            error.message
          }`
        );
        setError(true);
        setData(null);
        setLoading(false);
      }
      // AbortError: do nothing — either a new request is already in flight
      // (which manages its own loading state) or the component is unmounting.
    }
  }, [fileName, setData, setError, setLoading, url]);

  useEffect(() => {
    if (!skip) {
      fetchQuery();
    }

    // Cleanup: abort request on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchQuery, skip, url]);

  return {...fetchState, refetchQuery: fetchQuery};
};

export default useFetchData;
